import { useEffect, useState } from "react";
import { RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API, errMsg } from "@/lib/api";
import { STAGES, DELIVERED, DURATION, STYLE, FORMAT, egp, fmtDate, cardCls, orderLabel } from "@/lib/labels";
import { StageBar } from "@/components/account/StageTracker";
import { CancelOrderDialog } from "./CancelOrderDialog";
import { OrderMedia } from "./OrderMedia";

const FILTERS = { active: "شغالة", delivered: "اتسلمت", cancelled: "ملغية", all: "الكل" };
const match = (o, f) => f === "all" || (f === "cancelled" ? o.status === "cancelled" : o.status === "active" && (f === "delivered" ? o.stage === DELIVERED : o.stage < DELIVERED));

const OrderRow = ({ o, i, onChange, onCancel }) => {
  const setStage = async (v) => {
    try { onChange((await api.patch(`/admin/orders/${o.id}/stage`, { stage: Number(v) })).data); toast.success("المرحلة اتحدثت والعميل اتبلغ"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancelled = o.status === "cancelled";
  return (
    <article className={`${cardCls} p-5`} data-testid={`admin-order-${i}`}>
      <div className="flex flex-col gap-4 sm:flex-row">
        <a href={`${API}/admin/orders/${o.id}/logo`} target="_blank" rel="noopener noreferrer" className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white">
          {o.logo?.content_type?.startsWith("image/") && o.logo.content_type !== "image/svg+xml"
            ? <img src={`${API}/admin/orders/${o.id}/logo`} alt="" loading="lazy" className="h-full w-full object-contain p-1" /> : <span className="text-[10px] text-[var(--km-purple)]">تحميل</span>}
        </a>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-head text-lg font-bold text-white" data-testid={`admin-order-label-${i}`}>{orderLabel(o)} {o.is_free && <span className="rounded-full bg-[var(--km-cyan)] px-2 py-0.5 text-xs text-[#0b0414]">مجاني</span>}</p>
              <p className="text-sm text-purple-200/80">{DURATION[o.duration]} · {STYLE[o.style]} · {FORMAT[o.format]} · {o.is_free ? "0 ج.م" : egp(o.price)}</p>
              <p className="text-xs text-purple-300/60" dir="ltr" style={{ textAlign: "right" }}>{o.phone} · {o.email} · {fmtDate(o.created_at)}</p>
            </div>
            {cancelled ? <span className="rounded-full bg-red-500/20 px-3 py-1 text-xs font-bold text-red-200">ملغي</span> : (
              <div className="flex items-center gap-2">
                <Select value={String(o.stage)} onValueChange={setStage} dir="rtl">
                  <SelectTrigger className="h-10 w-52 rounded-full border-purple-500/30 bg-black/30 text-white" data-testid={`admin-order-stage-select-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-purple-500/30 bg-[var(--km-bg2)] text-white">
                    {STAGES.map((s, k) => <SelectItem key={k} value={String(k)} data-testid={`stage-option-${k}`}>{k + 1}. {s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {o.stage < DELIVERED && !o.loyalty_counted && <button onClick={() => onCancel(o)} className="grid h-10 w-10 place-items-center rounded-full bg-white/5 text-red-300 hover:bg-red-500 hover:text-white" aria-label="إلغاء واسترداد" data-testid={`admin-order-cancel-${i}`}><XCircle className="h-4 w-4" /></button>}
              </div>
            )}
          </div>
          <StageBar stage={o.stage} cancelled={cancelled} />
          <details className="text-sm text-purple-100/90">
            <summary className="cursor-pointer text-[var(--km-lilac)]">النقاط والملاحظات</summary>
            <p className="mt-2 whitespace-pre-wrap break-words">{o.key_points}</p>
            {o.notes && <p className="mt-2 whitespace-pre-wrap break-words text-purple-300/80">ملاحظات: {o.notes}</p>}
            {o.cancel_reason && <p className="mt-2 text-red-300">سبب الإلغاء: {o.cancel_reason}</p>}
            {cancelled && o.refund_percent !== undefined && <p className="mt-1 text-red-200" data-testid={`admin-order-refund-${i}`}>الاسترداد: {o.refund_percent}% · {egp(o.refund_amount ?? (o.is_free ? 0 : o.price))} · {o.cancelled_by} · {fmtDate(o.cancelled_at)}</p>}
          </details>
          {!cancelled && <OrderMedia o={o} i={i} onChange={onChange} />}
        </div>
      </div>
    </article>
  );
};

export const AdminOrders = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("active");
  const [cancelling, setCancelling] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const update = (d) => setItems((x) => x.map((y) => (y.id === d.id ? d : y)));

  const cancel = async (reason, refund_percent) => {
    try { update((await api.post(`/admin/orders/${cancelling.id}/cancel`, { reason, refund_percent })).data); toast.success(refund_percent ? `الأوردر اتلغى ورجع ${refund_percent}% لمحفظة العميل` : "الأوردر اتلغى من غير استرداد"); setCancelling(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (items === null) return <p className="text-purple-300/70">بنحمّل...</p>;
  const shown = items.filter((o) => match(o, filter));
  return (
    <section data-testid="admin-orders">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-3xl text-white">أوردرات الإعلانات <span className="text-[var(--km-pink)]">({shown.length})</span></h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries(FILTERS).map(([k, v]) => <button key={k} onClick={() => setFilter(k)} data-testid={`admin-orders-filter-${k}`} className={`rounded-full px-4 py-1.5 text-sm ${filter === k ? "bg-[var(--km-pink)] text-white" : "bg-white/10 text-purple-200"}`}>{v}</button>)}
          <button onClick={load} className="rounded-full bg-white/10 p-2 text-white" aria-label="تحديث" data-testid="admin-orders-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {shown.length === 0 ? <p className="rounded-3xl border-2 border-dashed border-purple-500/30 p-10 text-center text-purple-300/70" data-testid="admin-orders-empty">مفيش أوردرات هنا</p>
        : <div className="space-y-4">{shown.map((o, i) => <OrderRow key={o.id} o={o} i={i} onChange={update} onCancel={setCancelling} />)}</div>}
      <CancelOrderDialog order={cancelling} onClose={() => setCancelling(null)} onConfirm={cancel} />
    </section>
  );
};
